import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  const effectiveDate = 'February 16, 2026';
  const lastUpdated = 'February 16, 2026';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            &larr; Back to Kwikly
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">
            <strong>Effective Date:</strong> {effectiveDate} | <strong>Last Updated:</strong> {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-slate max-w-none">

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Kwikly (&quot;we,&quot; &quot;us,&quot; &quot;our&quot;). We are committed to protecting your privacy and being transparent about how we collect, use, and share your personal information.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Kwikly is an AI-powered job application platform that helps you discover jobs, tailor your resume, and manage your job search. This includes our web application (https://kwikly.me) and Chrome browser extension.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              This policy applies to users worldwide, including those in the United States, European Union, United Kingdom, and India. We comply with applicable privacy laws including the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and India&apos;s Digital Personal Data Protection Act 2023 (DPDP Act).
            </p>

            {/* Privacy by Design + Revenue Model Transparency */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800 text-sm">
                <strong>Our Privacy Commitment:</strong> We follow a &quot;privacy by design&quot; approach, collecting only the minimum data necessary to provide our services.
                <strong> How We Make Money:</strong> Kwikly generates revenue through subscription fees from users. We do NOT sell user data or rely on advertising revenue.
              </p>
            </div>
          </section>

          {/* Table of Contents */}
          <section className="mb-8 bg-gray-50 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Contents</h2>
            <ol className="text-primary-600 space-y-1 text-sm">
              <li><a href="#info-collect" className="hover:underline">1. Information We Collect</a></li>
              <li><a href="#how-use" className="hover:underline">2. How We Use Your Information</a></li>
              <li><a href="#how-share" className="hover:underline">3. How We Share Your Information</a></li>
              <li><a href="#international" className="hover:underline">4. International Data Transfers</a></li>
              <li><a href="#retention" className="hover:underline">5. Data Retention</a></li>
              <li><a href="#rights" className="hover:underline">6. Your Privacy Rights</a></li>
              <li><a href="#security" className="hover:underline">7. Security</a></li>
              <li><a href="#children" className="hover:underline">8. Children&apos;s Privacy</a></li>
              <li><a href="#cookies" className="hover:underline">9. Cookie Policy</a></li>
              <li><a href="#ccpa" className="hover:underline">10. California-Specific Disclosures (CCPA)</a></li>
              <li><a href="#gdpr" className="hover:underline">11. EU-Specific Disclosures (GDPR)</a></li>
              <li><a href="#dpdp" className="hover:underline">12. India-Specific Disclosures (DPDP Act)</a></li>
              <li><a href="#extension" className="hover:underline">13. Chrome Extension Disclosures</a></li>
              <li><a href="#changes" className="hover:underline">14. Changes to This Policy</a></li>
              <li><a href="#contact" className="hover:underline">15. Contact Us</a></li>
            </ol>
          </section>

          {/* Section 1: Information We Collect */}
          <section id="info-collect" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.1 Information You Provide</h3>
            <p className="text-gray-700 mb-3"><strong>Account Information:</strong> Email address, password (stored as encrypted hash), full name, profile information.</p>
            <p className="text-gray-700 mb-3"><strong>Resume Data:</strong> Resume files you upload, parsed content including work experience, education, skills, certifications, and contact information.</p>
            <p className="text-gray-700 mb-3"><strong>Job Data:</strong> Job postings you save via our Chrome extension, job titles, companies, descriptions, URLs, application status, and tailored resume versions.</p>
            <p className="text-gray-700 mb-3"><strong>Payment Information:</strong> Billing name and email. Payment card details are processed by Stripe (international) and Razorpay (India). We do NOT store complete credit card numbers.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.2 Information Collected Automatically</h3>
            <p className="text-gray-700 mb-3"><strong>Usage Data:</strong> Features used, actions taken, resume tailoring history, time spent on sections.</p>
            <p className="text-gray-700 mb-3"><strong>Technical Data:</strong> IP address, browser type, device type, operating system, referring URLs, pages viewed, access times.</p>
            <p className="text-gray-700 mb-3"><strong>Analytics:</strong> We use Google Analytics and PostHog to understand how users interact with our platform.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.3 Chrome Extension Data</h3>
            <p className="text-gray-700 mb-3">Our extension collects job posting information <strong>ONLY</strong> when you explicitly click &quot;Save Job&quot;.</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-3">
              <p className="text-green-800 font-medium mb-2">What the extension does:</p>
              <ul className="text-green-700 text-sm space-y-1 list-disc list-inside">
                <li>Extracts job posting content when you click &quot;Save Job&quot;</li>
                <li>Reads job URLs and metadata from LinkedIn and job boards</li>
                <li>Sends extracted job data to our servers</li>
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-3">
              <p className="text-red-800 font-medium mb-2">What the extension does NOT do:</p>
              <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                <li>Does NOT access your private messages or email</li>
                <li>Does NOT read your LinkedIn connections or network</li>
                <li>Does NOT automate any actions on LinkedIn</li>
                <li>Does NOT track browsing on other websites</li>
                <li>Does NOT run or collect data in the background</li>
                <li>Does NOT modify, interfere with, or break the functionality of job board websites</li>
              </ul>
            </div>
          </section>

          {/* Section 2: How We Use Information */}
          <section id="how-use" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 To Provide Our Services</h3>
            <p className="text-gray-700 mb-3">Create and manage your account, store resumes and job applications, process resume content for AI tailoring, generate customized resume versions, and track your job applications.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 AI Processing</h3>
            <p className="text-gray-700 mb-3">We send your resume content and job descriptions to Anthropic&apos;s Claude API to analyze skills, match qualifications to jobs, and generate tailored content.</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
              <p className="text-blue-800 text-sm"><strong>Important:</strong> Anthropic does not train their models on customer data submitted via their API. See <a href="https://www.anthropic.com/legal/privacy" className="underline" target="_blank" rel="noopener noreferrer">Anthropic&apos;s privacy policy</a>.</p>
            </div>
            <p className="text-gray-700 mt-3"><strong>Aggregated Data:</strong> We may use aggregated, anonymized usage patterns (not your individual resume content or personal information) to improve our AI matching algorithms. These patterns cannot be used to identify you.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.3 Payment Processing</h3>
            <p className="text-gray-700 mb-3">Process subscription payments via Stripe (international) and Razorpay (India), prevent fraud, and comply with tax/accounting requirements.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.4 Product Improvement</h3>
            <p className="text-gray-700 mb-3">Understand usage patterns, identify and fix issues, develop new features, and measure engagement.</p>
          </section>

          {/* Section 3: How We Share Information */}
          <section id="how-share" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Share Your Information</h2>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-semibold">We do NOT sell your personal information to third parties.</p>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Service Providers</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Provider</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Purpose</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Location</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr><td className="px-4 py-2 border-b">Anthropic (Claude API)</td><td className="px-4 py-2 border-b">AI resume tailoring</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2 border-b">Stripe</td><td className="px-4 py-2 border-b">International payments</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2 border-b">Razorpay</td><td className="px-4 py-2 border-b">India payments</td><td className="px-4 py-2 border-b">India</td></tr>
                  <tr><td className="px-4 py-2 border-b">MongoDB Atlas</td><td className="px-4 py-2 border-b">Database hosting</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2 border-b">Vercel</td><td className="px-4 py-2 border-b">Frontend hosting</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2 border-b">Render</td><td className="px-4 py-2 border-b">Backend hosting</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2 border-b">Google Analytics</td><td className="px-4 py-2 border-b">Web analytics</td><td className="px-4 py-2 border-b">USA</td></tr>
                  <tr><td className="px-4 py-2">PostHog</td><td className="px-4 py-2">Product analytics</td><td className="px-4 py-2">USA</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Legal Requirements</h3>
            <p className="text-gray-700">We may disclose information if required by law, regulation, legal process, or governmental request.</p>
          </section>

          {/* Section 4: International Transfers */}
          <section id="international" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. International Data Transfers</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">For EU/UK Users (GDPR)</h3>
            <p className="text-gray-700 mb-3">When transferring data outside the EEA/UK, we rely on EU Standard Contractual Clauses (SCCs) and service provider commitments to protect your data.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">For Indian Users (DPDP Act)</h3>
            <p className="text-gray-700">We may transfer data outside India to service providers in the USA. Such transfers comply with the DPDP Act requirements.</p>
          </section>

          {/* Section 5: Data Retention */}
          <section id="retention" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Active accounts:</strong> Data retained while your account is active.</li>
              <li><strong>Deleted accounts:</strong> Data deleted within <strong>90 days</strong> of deletion request.</li>
              <li><strong>Payment records:</strong> Retained for <strong>7 years</strong> for legal/tax compliance.</li>
              <li><strong>Analytics data:</strong> Aggregated/anonymized data may be retained indefinitely.</li>
            </ul>

            {/* Pre-deletion warning - competitor best practice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-amber-800 font-medium mb-2">Important: Before Deleting Your Account</p>
              <p className="text-amber-700 text-sm">
                Account deletion is <strong>permanent and irreversible</strong>. All your resumes, job applications, tailored versions, and data will be permanently deleted within 90 days and cannot be recovered.
                Please use the &quot;Export Data&quot; feature in Settings before deleting your account if you want to keep a copy of your data.
              </p>
            </div>

            <p className="text-gray-600 text-sm mt-3">
              <strong>Why 90 days?</strong> We retain data for 90 days to allow for backup system cleanup and to provide a grace period in case of accidental deletion. If you need immediate deletion, contact <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a>.
            </p>
          </section>

          {/* Section 6: Your Privacy Rights */}
          <section id="rights" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Your Privacy Rights</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">For EU/UK Users (GDPR)</h3>
            <ul className="text-gray-700 space-y-1 list-disc list-inside">
              <li>Right to Access - request a copy of your data</li>
              <li>Right to Rectification - correct inaccurate data</li>
              <li>Right to Erasure (&quot;Right to be Forgotten&quot;)</li>
              <li>Right to Restriction of Processing</li>
              <li>Right to Data Portability</li>
              <li>Right to Object to processing</li>
              <li>Right to Withdraw Consent</li>
              <li>Right to Lodge a Complaint with supervisory authority</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">For California Users (CCPA)</h3>
            <ul className="text-gray-700 space-y-1 list-disc list-inside">
              <li>Right to Know what data is collected</li>
              <li>Right to Delete personal information</li>
              <li>Right to Correct inaccurate information</li>
              <li>Right to Opt-Out of sale (we do NOT sell data)</li>
              <li>Right to Non-Discrimination</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">For Indian Users (DPDP Act)</h3>
            <ul className="text-gray-700 space-y-1 list-disc list-inside">
              <li>Right to Access your data</li>
              <li>Right to Correction of inaccurate data</li>
              <li>Right to Erasure</li>
              <li>Right to Grievance Redressal</li>
              <li>Right to Nominate someone to exercise your rights</li>
              <li>Right to Withdraw Consent</li>
            </ul>

            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <p className="text-gray-700 font-medium mb-2">To exercise your rights:</p>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>Email: <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a> or <a href="mailto:support@kwikly.me" className="text-primary-600 hover:underline">support@kwikly.me</a></li>
                <li>Account Settings: <Link to="/settings" className="text-primary-600 hover:underline">https://kwikly.me/settings</Link></li>
              </ul>
              <p className="text-gray-600 text-sm mt-2"><strong>Response Time:</strong> GDPR: 1 month | CCPA: 45 days | DPDP: As per rules</p>
              <p className="text-gray-600 text-sm mt-2"><strong>Data Export Formats:</strong> Your exported data will be provided in JSON format (machine-readable) or PDF format (human-readable), suitable for use with other resume building services.</p>
            </div>
          </section>

          {/* Section 7: Security */}
          <section id="security" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Security</h2>
            <p className="text-gray-700 mb-3">We implement appropriate technical and organizational measures:</p>
            <ul className="text-gray-700 space-y-1 list-disc list-inside">
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>Encryption at rest for sensitive data</li>
              <li>Password hashing using bcrypt algorithm</li>
              <li>JWT-based secure authentication</li>
              <li>Rate limiting and API security</li>
            </ul>
            <p className="text-gray-700 mt-3"><strong>Data Breach Response:</strong> We will notify affected users via email within 72 hours of discovery, as required by GDPR and DPDP Act.</p>
          </section>

          {/* Section 8: Children's Privacy */}
          <section id="children" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Children&apos;s Privacy</h2>
            <p className="text-gray-700">Kwikly is not intended for users under 16. We do not knowingly collect personal information from children. If you believe a child has provided us with data, please contact <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a>.</p>
          </section>

          {/* Section 9: Cookie Policy */}
          <section id="cookies" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookie Policy</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Types of Cookies</h3>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Strictly Necessary:</strong> Essential for authentication and security. Cannot be disabled.</li>
              <li><strong>Analytics:</strong> Google Analytics, PostHog - understand usage. Can opt out.</li>
              <li><strong>Functional:</strong> Remember preferences. Can opt out.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Managing Cookies</h3>
            <p className="text-gray-700 mb-2">You can control cookies through browser settings. We respect Global Privacy Control (GPC) signals.</p>
            <p className="text-gray-700">
              <strong>Opt-out:</strong> <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a>
            </p>
          </section>

          {/* Section 10: CCPA */}
          <section id="ccpa" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. California-Specific Disclosures (CCPA)</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Categories of Personal Information Collected</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Category</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Examples</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Collected?</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr><td className="px-4 py-2 border-b">Identifiers</td><td className="px-4 py-2 border-b">Name, email, IP address</td><td className="px-4 py-2 border-b">Yes</td></tr>
                  <tr><td className="px-4 py-2 border-b">Customer records</td><td className="px-4 py-2 border-b">Name, payment info</td><td className="px-4 py-2 border-b">Yes</td></tr>
                  <tr><td className="px-4 py-2 border-b">Commercial information</td><td className="px-4 py-2 border-b">Subscription history</td><td className="px-4 py-2 border-b">Yes</td></tr>
                  <tr><td className="px-4 py-2 border-b">Internet activity</td><td className="px-4 py-2 border-b">Usage data</td><td className="px-4 py-2 border-b">Yes</td></tr>
                  <tr><td className="px-4 py-2">Professional info</td><td className="px-4 py-2">Resume content, work history</td><td className="px-4 py-2">Yes</td></tr>
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <p className="text-green-800 font-semibold">We do NOT sell your personal information.</p>
            </div>
          </section>

          {/* Section 11: GDPR */}
          <section id="gdpr" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. EU-Specific Disclosures (GDPR)</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Data Controller</h3>
            <p className="text-gray-700 mb-3">Kwikly (operated as sole proprietorship)<br />Email: <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a></p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Legal Bases for Processing</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Processing Activity</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">Legal Basis</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600">
                  <tr><td className="px-4 py-2 border-b">Account creation</td><td className="px-4 py-2 border-b">Performance of contract</td></tr>
                  <tr><td className="px-4 py-2 border-b">Resume tailoring</td><td className="px-4 py-2 border-b">Performance of contract, consent</td></tr>
                  <tr><td className="px-4 py-2 border-b">Payment processing</td><td className="px-4 py-2 border-b">Performance of contract, legal obligation</td></tr>
                  <tr><td className="px-4 py-2 border-b">Product analytics</td><td className="px-4 py-2 border-b">Legitimate interests, consent</td></tr>
                  <tr><td className="px-4 py-2">Marketing emails</td><td className="px-4 py-2">Consent (opt-in)</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Supervisory Authority</h3>
            <p className="text-gray-700">You can lodge a complaint with your local data protection authority. Find yours at: <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a></p>
          </section>

          {/* Section 12: DPDP */}
          <section id="dpdp" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. India-Specific Disclosures (DPDP Act)</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Data Fiduciary</h3>
            <p className="text-gray-700 mb-3">Kwikly (individual/sole proprietor)<br />Email: <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a></p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Consent</h3>
            <p className="text-gray-700 mb-3">Your consent is free, specific, informed, unconditional, and unambiguous. You can withdraw consent at any time via Settings or by emailing us.</p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Grievance Redressal</h3>
            <p className="text-gray-700">Grievance Officer: <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a><br />
            If unresolved, you may file a complaint with the Data Protection Board of India.</p>
          </section>

          {/* Section 13: Chrome Extension */}
          <section id="extension" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Chrome Extension Disclosures</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Permissions Requested</h3>
            <ul className="text-gray-700 space-y-1 list-disc list-inside">
              <li><strong>activeTab:</strong> Read job posting from current tab when you click &quot;Save Job&quot;</li>
              <li><strong>storage:</strong> Store extension settings</li>
              <li><strong>scripting:</strong> Inject job capture functionality</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Data Collection Transparency</h3>
            <ul className="text-gray-700 space-y-1">
              <li><strong>When:</strong> ONLY when you click &quot;Save Job&quot;</li>
              <li><strong>What:</strong> Job title, company, description, URL, salary (if visible)</li>
              <li><strong>Not collected:</strong> Messages, connections, browsing history</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">User Control</h3>
            <p className="text-gray-700">Uninstall anytime from Chrome settings. Delete saved jobs from your account. Request complete deletion at <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a>.</p>
          </section>

          {/* Section 14: Changes */}
          <section id="changes" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Changes to This Policy</h2>
            <p className="text-gray-700 mb-3">We may update this policy from time to time.</p>
            <ul className="text-gray-700 space-y-1">
              <li><strong>Material changes:</strong> We will notify you via email at least 30 days before changes take effect.</li>
              <li><strong>Minor changes:</strong> We will update the &quot;Last Updated&quot; date.</li>
            </ul>
            <p className="text-gray-700 mt-3">Previous versions available upon request at <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a>.</p>
          </section>

          {/* Section 15: Contact */}
          <section id="contact" className="mb-8 scroll-mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-3">For questions, concerns, or requests regarding this Privacy Policy:</p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:privacy@kwikly.me" className="text-primary-600 hover:underline">privacy@kwikly.me</a> or <a href="mailto:support@kwikly.me" className="text-primary-600 hover:underline">support@kwikly.me</a><br />
                <strong>Website:</strong> <a href="https://kwikly.me" className="text-primary-600 hover:underline">https://kwikly.me</a>
              </p>
              <p className="text-gray-600 text-sm mt-3">
                <strong>Response Time:</strong> General inquiries within 5 business days. Privacy rights requests as specified in Section 6.
              </p>
            </div>
          </section>

          {/* Additional Resources */}
          <section className="mb-4 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Additional Resources</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-700 mb-2">Your Privacy Rights:</p>
                <ul className="text-gray-600 space-y-1">
                  <li><a href="https://gdpr.eu/" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">GDPR (EU)</a></li>
                  <li><a href="https://oag.ca.gov/privacy/ccpa" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">CCPA (California)</a></li>
                  <li><a href="https://www.meity.gov.in/data-protection-framework" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">DPDP Act (India)</a></li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-2">Third-Party Policies:</p>
                <ul className="text-gray-600 space-y-1">
                  <li><a href="https://www.anthropic.com/legal/privacy" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Anthropic Claude</a></li>
                  <li><a href="https://stripe.com/privacy" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe</a></li>
                  <li><a href="https://razorpay.com/privacy/" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Razorpay</a></li>
                </ul>
              </div>
            </div>
          </section>

          <p className="text-center text-gray-500 text-sm mt-8 border-t pt-6">
            Thank you for trusting Kwikly with your job search. We are committed to protecting your privacy.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t py-6">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-between items-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} Kwikly. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-primary-600">Terms of Service</Link>
            <Link to="/" className="hover:text-primary-600">Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
